import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Button, Checkbox, ColourSwatchesIcon, TrashIcon} from '@taopix/taopix-design-system';
import {NULL_COLOUR_HEX, AssetType, BoxType, BoxTypeShape} from '@taopix/taopix-core';
import {CheckboxProps} from '@taopix/taopix-design-system/dist/types/Components/Checkbox/Checkbox';
import BackgroundsList, {Tile} from '../../../BackgroundsList/BackgroundsList';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import ToolPaletteSection, {ToolPaletteSectionProps} from '../../../ToolPalette/ToolPaletteSection';

export type ColourProps = {
  id: string;
  sectionKey: string;
  item?: ComputedPageItem;
  buttonLabel: string;
  sectionTitle: string;
  selectedColour?: string | number;
  colours: Record<string, string>;
  onClick: (event: React.MouseEvent | React.ChangeEvent, selectedBackground: Tile) => void;
  parentSection: string;
  ignoreItem?: boolean;
  allowRemoveColour?: boolean;
  onRemoveColour?: () => void;
  allowTransparent?: boolean;
  useFocusMode?: boolean;
} & Pick<ToolPaletteSectionProps, 'smallScreenPanelHeight'> &
  Pick<CheckboxProps, 'preventManualUncheck'>;

const Colour = ({
  id,
  sectionKey,
  item,
  buttonLabel,
  sectionTitle,
  selectedColour,
  colours,
  onClick,
  parentSection,
  ignoreItem,
  allowRemoveColour,
  onRemoveColour,
  allowTransparent,
  preventManualUncheck,
  smallScreenPanelHeight = 'auto',
  useFocusMode,
}: ColourProps) => {
  const ui = useUiState();
  const {t} = useTranslation();

  const [showPalette, setShowPalette] = useState<boolean>();

  const isSmallScreen = ui.state.isSmallScreen;

  const transparent = {
    id: 'transparent',
    assetRef: 'transparent-AssetRef',
    src: NULL_COLOUR_HEX,
    categoryName: 'None',
    isColour: true,
    assetType: AssetType.Background,
    asset: undefined,
  };

  useEffect(() => {
    // for any other palettes other than box settings we want to ignore item and show palette regardless
    if (!ignoreItem) {
      const boxType = item?.type;
      if (item?.asset?.assetType === AssetType.Background) {
        setShowPalette(false);
        if (ui.state.palette.section?.includes(sectionKey)) {
          ui.dispatch(UserInterfaceActions.setToolPaletteSection('box-settings-palette'));
        }
      }
      if (
        boxType === BoxType.Image &&
        item?.asset?.assetType !== AssetType.Background &&
        item?.asset?.assetType !== 3
      ) {
        setShowPalette(true);
      }
      if (boxType === BoxType.Text) {
        if (ui.state.palette.section === 'box-settings-palette/shadow/shadow-colour') {
          setShowPalette(false);
          ui.dispatch(UserInterfaceActions.setToolPaletteSection('box-settings-palette'));
        } else {
          setShowPalette(true);
        }
      }
      if (
        boxType === BoxType.Shape &&
        [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
          item?.shape as BoxTypeShape
        )
      ) {
        setShowPalette(true);
      }
      if (
        item?.image?.originMask?.data !== null &&
        item?.asset?.assetType === AssetType.ScrapbookImage
      ) {
        setShowPalette(true);
      }
    } else {
      setShowPalette(true);
    }
  }, [item?.asset, item?.asset?.assetType, item?.type, item?.image, item?.shape]);

  const noneOptionClasses = classNames(
    'flex',
    '!flex-row',
    'space-x-sm',
    // Add the 'None' colour graphic (a white box with a red stripe through it)
    'before:w-[45px]',
    'before:h-[45px]',
    'before:bg-[linear-gradient(135deg,white,white_47%,red,white_calc(47%+3px),white)]',
    'before:bg-no-repeat',
    'before:border',
    'before:border-themeTextColour20',
    'before:bg-clip-content',
    // Position the text outside the label so it doesn't get wrapped in the selection border
    '[&_span]:absolute [&_span]:left-full [&_span]:h-full [&_span]:flex [&_span]:items-center'
  );

  const contentWrapperClasses = classNames(
    'lg:flex-1',
    'flex',
    'flex-col',
    'min-h-0',
    'px-md pb-[40rem] overflow-y-auto',
    smallScreenPanelHeight === 'auto' ? 'sm:h-mobilePaletteContentHeight' : 'sm:flex-1'
  );

  if (ignoreItem ? showPalette : showPalette && item?.ref !== -1) {
    return (
      <div className={'flex w-full justify-between'}>
        <p className={'flex items-center mr-sm'}>{buttonLabel}</p>
        {selectedColour && allowRemoveColour && onRemoveColour && (
          <Button
            data-testid={'colour-picker-removecolour-button'}
            startIcon={<TrashIcon />}
            label={t('*:str_LabelRemoveColour', {defaultValue: 'Remove colour'})}
            hideLabel
            buttonStyle={'standard'}
            className={'ml-auto mr-xs'}
            onClick={() => onRemoveColour()}
          />
        )}
        <ToolPaletteSection
          id={id}
          title={sectionTitle}
          buttonLayout={'icon-only'}
          buttonIcon={
            selectedColour === '' || selectedColour === undefined ? (
              <ColourSwatchesIcon />
            ) : selectedColour === NULL_COLOUR_HEX ? (
              <>
                <div className="flex-1 bg-[linear-gradient(135deg,white,white_47%,red,white_calc(47%+3px))] border border-themeTextColour20 bg-clip-content"></div>
              </>
            ) : (
              <>
                <div className="flex-1" style={{backgroundColor: `#${selectedColour}`}} />
              </>
            )
          }
          sectionKey={sectionKey}
          subSection={true}
          smallScreenPanelHeight={smallScreenPanelHeight}
          useFocusMode={useFocusMode}
          parentselected={true}
        >
          <div className={contentWrapperClasses} tabIndex={-1}>
            {allowTransparent && (
              <div className="flex my-md items-baseline">
                <Checkbox
                  className={noneOptionClasses}
                  defaultChecked={selectedColour === NULL_COLOUR_HEX}
                  label={t('str_LabelNone')}
                  hideLabel
                  id={'background-checkbox-transparent'}
                  appearance={'border'}
                  onChange={(isChecked: boolean, event: any) => {
                    onClick(event, transparent);
                  }}
                  preventManualUncheck
                />
              </div>
            )}
            <BackgroundsList
              visible={true}
              backgroundColours={colours}
              backgrounds={[]}
              columns={isSmallScreen ? 6 : 5}
              columnGap={3}
              filterValues={{}}
              useHexCode={true}
              orientation={'vertical'}
              selectedBackgroundRef={selectedColour as string}
              parentSection={parentSection}
              onClick={onClick}
              preventManualUncheck={preventManualUncheck}
            />
          </div>
        </ToolPaletteSection>
      </div>
    );
  } else {
    return <></>;
  }
};

export default Colour;
